'use client'

import type React from 'react'
import { useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'

type StepActionsProps = {
  onPrevious?: () => void
  onNext: () => void
  nextDisabled?: boolean
  previousLabel?: string
  nextLabel?: string
  /** Tailwind gradient classes, e.g. "from-blue-600 to-purple-600" */
  nextGradient?: string
  className?: string

  /** If provided, we’ll scroll the Next button into view inside this element */
  scrollContainerRef?: React.RefObject<HTMLElement | null>
  /** When the Next button becomes enabled, scroll it into view */
  scrollIntoViewWhenEnabled?: boolean
  /** Offset (px) for sticky headers etc. */
  scrollOffset?: number
}

const StepActions: React.FC<StepActionsProps> = ({
  onPrevious,
  onNext,
  nextDisabled = false,
  previousLabel = 'Previous',
  nextLabel = 'Next Step',
  nextGradient = 'from-blue-600 to-purple-600',
  className = '',
  scrollContainerRef,
  scrollIntoViewWhenEnabled = false,
  scrollOffset = 96,
}) => {
  const nextBtnRef = useRef<HTMLButtonElement | null>(null)

  // When next becomes enabled, scroll it into view (inside the dialog if provided)
  useEffect(() => {
    if (nextDisabled || !scrollIntoViewWhenEnabled) return
    const btn = nextBtnRef.current
    if (!btn) return

    const container =
      (scrollContainerRef?.current as HTMLElement | null) ??
      (btn.closest('[data-scroll-container]') as HTMLElement | null)

    requestAnimationFrame(() => {
      if (container) {
        const cRect = container.getBoundingClientRect()
        const bRect = btn.getBoundingClientRect()
        const top = container.scrollTop + (bRect.top - cRect.top) - scrollOffset
        container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
      } else {
        btn.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    })
  }, [
    nextDisabled,
    scrollIntoViewWhenEnabled,
    scrollContainerRef,
    scrollOffset,
  ])

  return (
    <div className={`flex items-center justify-between pt-6 ${className}`}>
      {onPrevious ? (
        <Button variant="outline" size="lg" onClick={onPrevious}>
          <ChevronLeft className="w-5 h-5 mr-2" />
          {previousLabel}
        </Button>
      ) : (
        <span />
      )}

      <Button
        ref={nextBtnRef}
        onClick={onNext}
        disabled={nextDisabled}
        size="lg"
        className={`bg-gradient-to-r ${nextGradient} text-white px-8 py-3 rounded-full shadow-lg disabled:opacity-50`}
      >
        {nextLabel}
        <ChevronRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  )
}

export default StepActions
